/**
 * @module AssetContext
 * @description Contexto global para la gestión del inventario de activos de información
 * según ISO 27001/27002/27005.
 *
 * ## Entidades gestionadas
 * El contexto cubre el ciclo de vida completo de los activos de información:
 *
 * | Entidad                | Tabla BD                    | Propósito |
 * |------------------------|-----------------------------|-----------|
 * | `InformationAsset`     | `information_assets`        | Inventario con triada CIA y criticidad |
 * | `AssetVulnerability`   | `asset_vulnerabilities`     | Vulnerabilidades detectadas por activo |
 * | `AssetThreat`          | `asset_threats`             | Amenazas identificadas (ISO 27005) |
 * | `AssetRisk`            | `asset_risks`               | Evaluación riskLevel = impacto × probabilidad |
 * | `AssetControlMapping`  | `asset_control_mappings`    | Relación activos ↔ controles ISO |
 * | `AssetLifecycleEvent`  | `asset_lifecycle_events`    | Historial de cambios de estado |
 * | `AssetChangeManagement`| `asset_change_management`   | Gestión de cambios (ISO 27002 A.8.32) |
 *
 * ## Criticidad calculada por PostgreSQL
 * La columna `criticality` de `information_assets` es calculada por un trigger en PostgreSQL
 * basado en la triada CIA (`confidentiality_impact`, `integrity_impact`, `availability_impact`):
 *   - score = max(C, I, A)
 *   - ≥4 = 'critical', ≥3 = 'high', ≥2 = 'medium', <2 = 'low'
 * Por esto, `createAsset` no recibe `criticality` — es un campo `Omit`.
 *
 * ## Filtrado local (sin queries)
 * `filteredAssets` y `metrics` son valores `useMemo` calculados sobre los datos
 * ya cargados en el estado React. Esto evita queries adicionales para cada
 * cambio de filtro, manteniendo la UI reactiva sin latencia.
 *
 * ## Historial de ciclo de vida
 * `logLifecycleEvent()` es un helper interno que registra automáticamente eventos
 * en `asset_lifecycle_events` tras operaciones CRUD. Es una llamada fire-and-forget:
 * los errores solo generan un log sin interrumpir el flujo principal.
 *
 * ## Gestión de cambios (ISO 27002 A.8.32)
 * Solo aplica a activos de tipo `software`. El ciclo completo es:
 * requested → approved → in_progress → completed → verified
 * Con opción de `rolled_back` en cualquier estado post-aprobación.
 *
 * @see src/components/Assets/ — componentes de UI del módulo
 * @see supabase/migrations/ — esquema de tablas de activos
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { useAuth } from './AuthContext';
import type {
  InformationAsset,
  AssetVulnerability,
  AssetThreat,
  AssetRisk,
  AssetControlMapping,
  AssetLifecycleEvent,
  AssetMetrics,
  AssetFilter,
  AssetType,
  AssetClassification,
  AssetLifecycleStatus,
  DRPPlan,
  User,
  AssetChangeManagement
} from '../types/iso';

// =====================================================
// INTERFACES Y TIPOS
// =====================================================

/**
 * Contrato del contexto de activos expuesto a los componentes consumidores.
 * Organizado en módulos funcionales para claridad en IntelliSense.
 */
interface AssetContextType {
  // ── Estados principales ──────────────────────────────────────────────────
  assets: InformationAsset[];
  vulnerabilities: AssetVulnerability[];
  threats: AssetThreat[];
  risks: AssetRisk[];
  controlMappings: AssetControlMapping[];

  // ── Estados de UI ────────────────────────────────────────────────────────
  isLoading: boolean;
  error: string | null;
  filter: AssetFilter;
  searchQuery: string;

  // ── Funciones de utilidad ────────────────────────────────────────────────
  clearError: () => void;
  refreshData: () => Promise<void>;
  /** Actualiza uno o más campos del filtro activo (merge superficial) */
  setFilter: (filter: Partial<AssetFilter>) => void;
  /** Restaura el filtro a su estado inicial (sin filtros activos) */
  resetFilter: () => void;
  setSearchQuery: (query: string) => void;

  // ── Activos filtrados (valor derivado calculado con useMemo) ─────────────
  filteredAssets: InformationAsset[];

  // ── Métricas (valor derivado calculado con useMemo) ──────────────────────
  metrics: AssetMetrics;

  // ── CRUD Activos ─────────────────────────────────────────────────────────
  loadAssets: () => Promise<void>;
  /** Crea activo sin `criticality` — PostgreSQL la calcula con trigger CIA */
  createAsset: (asset: Omit<InformationAsset, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'criticality'>) => Promise<InformationAsset>;
  updateAsset: (assetId: string, updates: Partial<InformationAsset>) => Promise<void>;
  deleteAsset: (assetId: string) => Promise<void>;
  getAssetById: (assetId: string) => InformationAsset | undefined;

  // ── CRUD Vulnerabilidades ────────────────────────────────────────────────
  /** Si `assetId` se omite, carga todas las vulnerabilidades de la organización */
  loadVulnerabilities: (assetId?: string) => Promise<void>;
  createVulnerability: (vulnerability: Omit<AssetVulnerability, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<void>;
  updateVulnerability: (vulnerabilityId: string, updates: Partial<AssetVulnerability>) => Promise<void>;
  /** Shortcut: marca como resolved con notas y resolved_date = hoy */
  resolveVulnerability: (vulnerabilityId: string, notes: string) => Promise<void>;
  deleteVulnerability: (vulnerabilityId: string) => Promise<void>;

  // ── CRUD Amenazas ────────────────────────────────────────────────────────
  loadThreats: (assetId?: string) => Promise<void>;
  createThreat: (threat: Omit<AssetThreat, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<void>;
  updateThreat: (threatId: string, updates: Partial<AssetThreat>) => Promise<void>;
  deleteThreat: (threatId: string) => Promise<void>;

  // ── CRUD Riesgos ─────────────────────────────────────────────────────────
  /** `riskLevel` y `riskCategory` son calculados por PostgreSQL (impacto × probabilidad) */
  loadRisks: (assetId?: string) => Promise<void>;
  createRisk: (risk: Omit<AssetRisk, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'riskLevel' | 'riskCategory'>) => Promise<void>;
  updateRisk: (riskId: string, updates: Partial<AssetRisk>) => Promise<void>;
  deleteRisk: (riskId: string) => Promise<void>;

  // ── Mapeo de Controles ───────────────────────────────────────────────────
  loadControlMappings: (assetId?: string) => Promise<void>;
  linkControlToAsset: (assetId: string, controlId: string, coverageStatus: string, notes?: string) => Promise<void>;
  updateControlMapping: (mappingId: string, updates: Partial<AssetControlMapping>) => Promise<void>;
  unlinkControlFromAsset: (mappingId: string) => Promise<void>;

  // ── Ciclo de vida ────────────────────────────────────────────────────────
  /** Carga y retorna el historial de eventos de un activo (no actualiza estado global) */
  loadLifecycleEvents: (assetId: string) => Promise<AssetLifecycleEvent[]>;
  createLifecycleEvent: (assetId: string, eventType: AssetLifecycleEvent['eventType'], description?: string, notes?: string) => Promise<void>;
  changeAssetStatus: (assetId: string, newStatus: AssetLifecycleStatus, notes?: string) => Promise<void>;

  // ── Gestión de Cambios (ISO 27002 A.8.32) — Solo para software ───────────
  loadAssetChanges: (assetId: string) => Promise<AssetChangeManagement[]>;
  createChange: (change: Omit<AssetChangeManagement, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>) => Promise<AssetChangeManagement>;
  updateChange: (changeId: string, updates: Partial<AssetChangeManagement>) => Promise<AssetChangeManagement>;
  /** Aprueba un cambio y registra al aprobador y timestamp */
  approveChange: (changeId: string, notes?: string) => Promise<AssetChangeManagement>;
  /** Marca el cambio como completado con resultados reales de implementación */
  completeChange: (changeId: string, results: { actualStart?: string; actualEnd?: string; testingResults?: string; notes?: string }) => Promise<AssetChangeManagement>;
  /** Verifica la efectividad del cambio implementado (→ verified) */
  verifyChange: (changeId: string, notes?: string) => Promise<AssetChangeManagement>;
  /** Revierte el cambio con motivo de rollback (→ rolled_back) */
  rollbackChange: (changeId: string, reason: string) => Promise<AssetChangeManagement>;
}

// =====================================================
// FILTRO INICIAL
// =====================================================

/**
 * Estado inicial del filtro sin criterios activos.
 * Arrays vacíos = "sin filtro por esa dimensión" (no filtra = muestra todos).
 * Los campos booleanos `null` = "sin filtro" (no aplica el criterio).
 */
const initialFilter: AssetFilter = {
  assetType: [],
  classification: [],
  criticality: [],
  status: [],
  owner: [],
  department: [],
  hasOpenVulnerabilities: null,
  hasHighRisks: null,
  needsReview: null,
  dateRange: {
    start: '',
    end: ''
  }
};

// =====================================================
// MÉTRICAS INICIALES
// =====================================================

/**
 * Estado inicial de métricas con todos los contadores en cero.
 * Se usa como valor inicial del estado hasta que `useMemo` calcule
 * los valores reales cuando los arrays de datos estén poblados.
 * Garantiza que los componentes que consumen `metrics` nunca reciben `null`.
 */
const initialMetrics: AssetMetrics = {
  totalAssets: 0,
  activeAssets: 0,
  retiredAssets: 0,
  byType: { hardware: 0, software: 0, data: 0, service: 0, people: 0 },
  byClassification: { public: 0, internal: 0, confidential: 0, restricted: 0 },
  byCriticality: { low: 0, medium: 0, high: 0, critical: 0 },
  byStatus: { proposed: 0, approved: 0, active: 0, maintenance: 0, obsolete: 0, retired: 0 },
  totalVulnerabilities: 0,
  openVulnerabilities: 0,
  criticalVulnerabilities: 0,
  vulnerabilitiesBySeverity: { critical: 0, high: 0, medium: 0, low: 0 },
  totalRisks: 0,
  highRisks: 0,
  risksWithoutTreatment: 0,
  risksByCategory: { low: 0, medium: 0, high: 0, critical: 0 },
  risksByTreatment: { mitigate: 0, accept: 0, transfer: 0, avoid: 0 },
  assetsWithControls: 0,
  assetsWithoutControls: 0,
  averageControlsPerAsset: 0,
  assetsWithoutOwner: 0,
  assetsNeedingReview: 0,
  assetsWithOpenVulnerabilities: 0,
  criticalAssetsWithHighRisks: 0,
  assetsWithoutDRP: 0,
  obsoleteAssets: 0,
  assetsWithCriticalVulnerabilities: 0,
  confidentialAssetsWithoutControls: 0,
  assetsCreatedLast30Days: 0,
  assetsRetiredLast30Days: 0,
  vulnerabilitiesResolvedLast30Days: 0
};

// =====================================================
// CONTEXTO
// =====================================================

/** Contexto React interno — consumir mediante `useAssets()`. */
const AssetContext = createContext<AssetContextType | undefined>(undefined);

// =====================================================
// PROVIDER
// =====================================================

/**
 * Proveedor del contexto de activos. Debe estar dentro de `AuthProvider`.
 *
 * @param props.children - Árbol de componentes React que consumirán el contexto
 */
export function AssetProvider({ children }: { children: React.ReactNode }) {
  const { user, dbUserReady } = useAuth();

  // ── Estados principales ──────────────────────────────────────────────────
  const [assets, setAssets] = useState<InformationAsset[]>([]);
  const [vulnerabilities, setVulnerabilities] = useState<AssetVulnerability[]>([]);
  const [threats, setThreats] = useState<AssetThreat[]>([]);
  const [risks, setRisks] = useState<AssetRisk[]>([]);
  const [controlMappings, setControlMappings] = useState<AssetControlMapping[]>([]);

  // ── Estados de UI ────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilterState] = useState<AssetFilter>(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  /** Limpia el error actual — llamado desde UI tras mostrar el mensaje. */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Centraliza el manejo de errores del módulo de activos.
   * Detecta errores de tabla faltante (código `does not exist` / `relation`) para
   * mostrar un mensaje específico sobre la migración SQL pendiente.
   *
   * @param err     - Error capturado (tipo unknown del catch)
   * @param context - Nombre de la operación para el log
   */
  const handleError = useCallback((err: unknown, context: string) => {
    const errorMessage = err instanceof Error ? err.message : `Error en ${context}`;
    logger.error(`[ASSET ERROR] ${context}:`, errorMessage, err);

    // Detectar tablas de activos no creadas — migración SQL pendiente
    if (errorMessage.includes('does not exist') || errorMessage.includes('relation')) {
      logger.error(`[ASSET] ⚠️ TABLA NO EXISTE - Necesitas ejecutar la migración SQL en Supabase`);
      console.error('🔴 ERROR: Las tablas de activos no existen. Ejecuta la migración en Supabase SQL Editor.');
    }

    setError(errorMessage);
  }, []);

  /**
   * Actualiza el filtro activo con un merge superficial.
   * Solo modifica los campos presentes en `newFilter`, preservando el resto.
   *
   * @param newFilter - Campos del filtro a actualizar
   */
  const setFilter = useCallback((newFilter: Partial<AssetFilter>) => {
    setFilterState(prev => ({ ...prev, ...newFilter }));
  }, []);

  /**
   * Restaura el filtro a su estado inicial (sin criterios activos).
   * Equivale a "Limpiar filtros" en la UI.
   */
  const resetFilter = useCallback(() => {
    setFilterState(initialFilter);
  }, []);

  // =====================================================
  // HELPER: Formatear usuario
  // =====================================================

  /**
   * Transforma un objeto de usuario de la BD (resultado de JOIN) al tipo `User` de la app.
   * Usado en la transformación de activos, vulnerabilidades, riesgos y eventos.
   *
   * @param userData - Objeto con campos del usuario desde un JOIN de Supabase
   * @returns Objeto `User` tipado, o `undefined` si `userData` es null
   */
  const formatUser = (userData: Record<string, unknown> | null): User | undefined => {
    if (!userData) return undefined;
    return {
      id: userData.id as string,
      name: userData.name as string,
      email: userData.email as string,
      role: userData.role as 'admin' | 'technical_referent' | 'auditor' | 'viewer',
      unit: userData.unit as string,
      createdAt: '',  // Campo requerido por el tipo User, no disponible en JOINs selectivos
      isActive: true
    };
  };

  // =====================================================
  // HELPER: Registrar evento automático en historial
  // =====================================================

  /**
   * Registra un evento en el historial del ciclo de vida de un activo.
   * Llamada fire-and-forget: los errores solo generan un log sin interrumpir
   * el flujo principal (el activo ya fue creado/actualizado cuando esto falla).
   *
   * Usado internamente por `createVulnerability`, `deleteThreat`, `createRisk`,
   * `linkControlToAsset`, `unlinkControlFromAsset` para trazar automáticamente
   * todos los cambios relevantes en el historial del activo.
   *
   * @param assetId        - UUID del activo cuyo historial se actualizará
   * @param eventType      - Tipo de evento según el enum de `AssetLifecycleEvent`
   * @param description    - Descripción legible del evento
   * @param additionalData - Valores anteriores/nuevos para trazabilidad detallada
   */
  const logLifecycleEvent = useCallback(async (
    assetId: string,
    eventType: AssetLifecycleEvent['eventType'],
    description: string,
    additionalData?: { previousValue?: string; newValue?: string; notes?: string }
  ) => {
    if (!user?.organization_id) {
      console.warn('[ASSET] ⚠️ No se puede registrar evento: usuario no autenticado');
      return;
    }

    try {
      console.log('[ASSET] 📋 Intentando registrar evento:', { assetId, eventType, description });

      const { error: insertError } = await supabase
        .from('asset_lifecycle_events')
        .insert({
          organization_id: user.organization_id,
          asset_id: assetId,
          event_type: eventType,
          description: description,
          notes: additionalData?.notes || null,
          previous_value: additionalData?.previousValue || null,
          new_value: additionalData?.newValue || null,
          performed_by_id: user.id,
          performed_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('[ASSET] ❌ Error insertando evento en historial:', insertError);
        return; // Solo logear, no relanzar — es un efecto secundario, no operación principal
      }

      console.log('[ASSET] ✅ Evento registrado en historial:', eventType);
    } catch (err) {
      // Solo log — no interrumpimos el flujo principal si el historial falla
      console.error('[ASSET] ❌ Excepción registrando evento en historial:', err);
    }
  }, [user]);

  // =====================================================
  // CRUD ACTIVOS
  // =====================================================

  /**
   * Carga todos los activos de información de la organización con JOIN al propietario.
   * Extrae `drpPlan` desde el campo JSONB `metadata.drp` para facilitar el acceso
   * al Plan de Recuperación ante Desastres sin hacer una query separada.
   *
   * Los JOINs con FKs nominadas son necesarios porque `information_assets`
   * puede tener múltiples referencias a `users` (owner, created_by, etc.).
   */
  const loadAssets = useCallback(async () => {
    if (!user?.organization_id) return;

    try {
      console.log('[ASSET] 🔄 Consultando tabla information_assets...');

      const { data, error: queryError } = await supabase
        .from('information_assets')
        .select(`
          *,
          owner:users!information_assets_owner_id_fkey (id, name, email, role, unit)
        `)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });

      if (queryError) throw queryError;

      const formattedAssets: InformationAsset[] = (data || []).map((asset: Record<string, unknown>) => {
        // Extraer drpPlan desde metadata.drp — el DRP se guarda anidado en el JSONB
        const metadata = asset.metadata as Record<string, unknown> | undefined;
        const drpPlan = metadata?.drp as DRPPlan | undefined;

        return {
          id: asset.id as string,
          organizationId: asset.organization_id as string,
          code: asset.code as string,
          name: asset.name as string,
          description: asset.description as string | undefined,
          assetType: asset.asset_type as AssetType,
          classification: asset.classification as AssetClassification,
          confidentialityImpact: asset.confidentiality_impact as number,
          integrityImpact: asset.integrity_impact as number,
          availabilityImpact: asset.availability_impact as number,
          // criticality es calculada por PostgreSQL — viene directamente de la BD
          criticality: asset.criticality as 'low' | 'medium' | 'high' | 'critical',
          criticalityJustification: asset.criticality_justification as string | undefined,
          status: asset.status as AssetLifecycleStatus,
          ownerId: asset.owner_id as string,
          owner: formatUser(asset.owner as Record<string, unknown>),
          userIds: (asset.user_ids as string[]) || [],
          location: asset.location as string | undefined,
          department: asset.department as string | undefined,
          acquisitionDate: asset.acquisition_date as string | undefined,
          acquisitionCost: asset.acquisition_cost as number | undefined,
          vendor: asset.vendor as string | undefined,
          lastReviewDate: asset.last_review_date as string | undefined,
          nextReviewDate: asset.next_review_date as string | undefined,
          reviewFrequencyDays: asset.review_frequency_days as number | undefined,
          retirementDate: asset.retirement_date as string | undefined,
          parentId: asset.parent_id as string | undefined,
          metadata: metadata,
          drpPlan: drpPlan, // Extraído del JSONB para acceso directo
          createdAt: asset.created_at as string,
          updatedAt: asset.updated_at as string,
          createdBy: asset.created_by as string | undefined,
          updatedBy: asset.updated_by as string | undefined
        };
      });

      setAssets(formattedAssets);
      console.log(`[ASSET] ✅ Cargados ${formattedAssets.length} activos de información`);
    } catch (err) {
      console.error('[ASSET] ❌ Error cargando activos:', err);
      handleError(err, 'cargar activos');
    }
  }, [user, handleError]);

  /**
   * Crea un nuevo activo de información en el inventario.
   * Omite `criticality` porque PostgreSQL la calcula automáticamente con un trigger
   * basado en los valores CIA proporcionados (`confidentialityImpact`, etc.).
   *
   * @param asset - Datos del activo sin campos calculados/generados por PostgreSQL
   * @returns El activo creado con `id`, `criticality` y timestamps de la BD
   */
  const createAsset = useCallback(async (
    asset: Omit<InformationAsset, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'criticality'>
  ): Promise<InformationAsset> => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 📝 Creando activo...', asset);

      const { data, error: insertError } = await supabase
        .from('information_assets')
        .insert({
          organization_id: user.organization_id,
          code: asset.code,
          name: asset.name,
          description: asset.description,
          asset_type: asset.assetType,
          classification: asset.classification,
          confidentiality_impact: asset.confidentialityImpact,
          integrity_impact: asset.integrityImpact,
          availability_impact: asset.availabilityImpact,
          criticality_justification: asset.criticalityJustification,
          status: asset.status || 'proposed',
          owner_id: asset.ownerId,
          user_ids: asset.userIds || [],
          location: asset.location,
          department: asset.department,
          acquisition_date: asset.acquisitionDate,
          acquisition_cost: asset.acquisitionCost,
          vendor: asset.vendor,
          last_review_date: asset.lastReviewDate,
          next_review_date: asset.nextReviewDate,
          review_frequency_days: asset.reviewFrequencyDays || 365,
          parent_id: asset.parentId,
          metadata: asset.metadata || {},
          created_by: user.id
        })
        .select()
        .single();

      if (insertError) throw insertError;

      logger.log('[ASSET] ✅ Activo creado:', data);
      // Recargar para obtener el activo completo con owner JOIN y criticality calculada
      await loadAssets();

      return {
        ...asset,
        id: data.id,
        organizationId: data.organization_id,
        criticality: data.criticality, // Valor calculado por el trigger PostgreSQL
        createdAt: data.created_at,
        updatedAt: data.updated_at
      } as InformationAsset;
    } catch (err) {
      handleError(err, 'crear activo');
      throw err;
    }
  }, [user, loadAssets, handleError]);

  /**
   * Actualiza los campos de un activo existente.
   * Incluye `updated_by: user.id` en todos los updates para el trail de auditoría.
   * Solo incluye campos explícitamente proporcionados (patrón defensivo `!== undefined`).
   *
   * @param assetId - UUID del activo
   * @param updates - Campos del activo en camelCase a actualizar
   */
  const updateAsset = useCallback(async (assetId: string, updates: Partial<InformationAsset>) => {
    if (!user?.id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 🔄 Actualizando activo:', assetId, updates);

      // Siempre incluir updated_by para el trail de auditoría
      const dbUpdates: Record<string, unknown> = { updated_by: user.id };
      if (updates.code !== undefined) dbUpdates.code = updates.code;
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.assetType !== undefined) dbUpdates.asset_type = updates.assetType;
      if (updates.classification !== undefined) dbUpdates.classification = updates.classification;
      if (updates.confidentialityImpact !== undefined) dbUpdates.confidentiality_impact = updates.confidentialityImpact;
      if (updates.integrityImpact !== undefined) dbUpdates.integrity_impact = updates.integrityImpact;
      if (updates.availabilityImpact !== undefined) dbUpdates.availability_impact = updates.availabilityImpact;
      if (updates.criticalityJustification !== undefined) dbUpdates.criticality_justification = updates.criticalityJustification;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.ownerId !== undefined) dbUpdates.owner_id = updates.ownerId;
      if (updates.userIds !== undefined) dbUpdates.user_ids = updates.userIds;
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.department !== undefined) dbUpdates.department = updates.department;
      if (updates.acquisitionDate !== undefined) dbUpdates.acquisition_date = updates.acquisitionDate;
      if (updates.acquisitionCost !== undefined) dbUpdates.acquisition_cost = updates.acquisitionCost;
      if (updates.vendor !== undefined) dbUpdates.vendor = updates.vendor;
      if (updates.lastReviewDate !== undefined) dbUpdates.last_review_date = updates.lastReviewDate;
      if (updates.nextReviewDate !== undefined) dbUpdates.next_review_date = updates.nextReviewDate;
      if (updates.reviewFrequencyDays !== undefined) dbUpdates.review_frequency_days = updates.reviewFrequencyDays;
      if (updates.retirementDate !== undefined) dbUpdates.retirement_date = updates.retirementDate;
      if (updates.parentId !== undefined) dbUpdates.parent_id = updates.parentId;
      if (updates.metadata !== undefined) dbUpdates.metadata = updates.metadata;

      const { error: updateError } = await supabase
        .from('information_assets')
        .update(dbUpdates)
        .eq('id', assetId);

      if (updateError) throw updateError;

      logger.log('[ASSET] ✅ Activo actualizado');
      await loadAssets();
    } catch (err) {
      handleError(err, 'actualizar activo');
      throw err;
    }
  }, [user, loadAssets, handleError]);

  /**
   * Elimina un activo del inventario.
   * Si el activo tiene vulnerabilidades, riesgos o eventos de ciclo de vida,
   * estos se eliminan en cascada dependiendo de la configuración de FK en la BD.
   *
   * @param assetId - UUID del activo a eliminar
   */
  const deleteAsset = useCallback(async (assetId: string) => {
    try {
      logger.log('[ASSET] 🗑️ Eliminando activo:', assetId);

      const { error: deleteError } = await supabase
        .from('information_assets')
        .delete()
        .eq('id', assetId);

      if (deleteError) throw deleteError;

      logger.log('[ASSET] ✅ Activo eliminado');
      await loadAssets();
    } catch (err) {
      handleError(err, 'eliminar activo');
      throw err;
    }
  }, [loadAssets, handleError]);

  /**
   * Busca un activo por ID en el array local (O(n), sin query a BD).
   *
   * @param assetId - UUID del activo
   * @returns El activo si existe en el estado, `undefined` si no
   */
  const getAssetById = useCallback((assetId: string): InformationAsset | undefined => {
    return assets.find(a => a.id === assetId);
  }, [assets]);

  // =====================================================
  // CRUD VULNERABILIDADES
  // =====================================================

  /**
   * Carga vulnerabilidades de la organización.
   * Si `assetId` se proporciona, filtra solo las del activo específico.
   * El JOIN con `remediation_responsible` usa FK nominada para desambiguar
   * múltiples referencias a `users`.
   *
   * @param assetId - UUID del activo para filtrar (opcional — sin arg = todas)
   */
  const loadVulnerabilities = useCallback(async (assetId?: string) => {
    if (!user?.organization_id) return;

    try {
      console.log('[ASSET] 🔄 Consultando tabla asset_vulnerabilities...');

      let query = supabase
        .from('asset_vulnerabilities')
        .select(`
          *,
          remediation_responsible:users!asset_vulnerabilities_remediation_responsible_id_fkey (id, name, email, role, unit)
        `)
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });

      // Filtro por activo específico si se proporcionó
      if (assetId) {
        query = query.eq('asset_id', assetId);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      const formattedVulnerabilities: AssetVulnerability[] = (data || []).map((v: Record<string, unknown>) => ({
        id: v.id as string,
        organizationId: v.organization_id as string,
        assetId: v.asset_id as string,
        title: v.title as string,
        description: v.description as string | undefined,
        severity: v.severity as 'critical' | 'high' | 'medium' | 'low',
        detectedDate: v.detected_date as string,
        resolvedDate: v.resolved_date as string | undefined,
        status: v.status as 'open' | 'in_remediation' | 'resolved' | 'accepted',
        remediationNotes: v.remediation_notes as string | undefined,
        remediationResponsibleId: v.remediation_responsible_id as string | undefined,
        remediationResponsible: formatUser(v.remediation_responsible as Record<string, unknown>),
        externalReference: v.external_reference as string | undefined,
        metadata: v.metadata as Record<string, unknown> | undefined,
        createdAt: v.created_at as string,
        updatedAt: v.updated_at as string,
        createdBy: v.created_by as string | undefined
      }));

      setVulnerabilities(formattedVulnerabilities);
      console.log(`[ASSET] ✅ Cargadas ${formattedVulnerabilities.length} vulnerabilidades`);
    } catch (err) {
      console.error('[ASSET] ❌ Error cargando vulnerabilidades:', err);
      handleError(err, 'cargar vulnerabilidades');
    }
  }, [user, handleError]);

  /**
   * Registra una nueva vulnerabilidad en el activo.
   * Automáticamente registra un evento en el historial del activo tras la creación.
   *
   * @param vulnerability - Datos de la vulnerabilidad sin campos generados por PostgreSQL
   */
  const createVulnerability = useCallback(async (
    vulnerability: Omit<AssetVulnerability, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>
  ) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 📝 Creando vulnerabilidad...', vulnerability);

      const { error: insertError } = await supabase
        .from('asset_vulnerabilities')
        .insert({
          organization_id: user.organization_id,
          asset_id: vulnerability.assetId,
          title: vulnerability.title,
          description: vulnerability.description,
          severity: vulnerability.severity,
          detected_date: vulnerability.detectedDate,
          status: vulnerability.status || 'open',
          remediation_notes: vulnerability.remediationNotes,
          remediation_responsible_id: vulnerability.remediationResponsibleId,
          external_reference: vulnerability.externalReference,
          metadata: vulnerability.metadata || {},
          created_by: user.id
        });

      if (insertError) throw insertError;

      logger.log('[ASSET] ✅ Vulnerabilidad creada');

      // Registrar evento en historial del activo (fire-and-forget — no bloquea si falla)
      await logLifecycleEvent(
        vulnerability.assetId,
        'vulnerability_added',
        `Vulnerabilidad agregada: ${vulnerability.title}`,
        { newValue: `${vulnerability.severity} - ${vulnerability.title}` }
      );

      await loadVulnerabilities();
    } catch (err) {
      handleError(err, 'crear vulnerabilidad');
      throw err;
    }
  }, [user, loadVulnerabilities, handleError, logLifecycleEvent]);

  /**
   * Actualiza los campos de una vulnerabilidad existente.
   *
   * @param vulnerabilityId - UUID de la vulnerabilidad
   * @param updates         - Campos a actualizar en camelCase
   */
  const updateVulnerability = useCallback(async (vulnerabilityId: string, updates: Partial<AssetVulnerability>) => {
    try {
      logger.log('[ASSET] 🔄 Actualizando vulnerabilidad:', vulnerabilityId, updates);

      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.severity !== undefined) dbUpdates.severity = updates.severity;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.resolvedDate !== undefined) dbUpdates.resolved_date = updates.resolvedDate;
      if (updates.remediationNotes !== undefined) dbUpdates.remediation_notes = updates.remediationNotes;
      if (updates.remediationResponsibleId !== undefined) dbUpdates.remediation_responsible_id = updates.remediationResponsibleId;
      if (updates.externalReference !== undefined) dbUpdates.external_reference = updates.externalReference;

      const { error: updateError } = await supabase
        .from('asset_vulnerabilities')
        .update(dbUpdates)
        .eq('id', vulnerabilityId);

      if (updateError) throw updateError;

      logger.log('[ASSET] ✅ Vulnerabilidad actualizada');
      await loadVulnerabilities();
    } catch (err) {
      handleError(err, 'actualizar vulnerabilidad');
      throw err;
    }
  }, [loadVulnerabilities, handleError]);

  /**
   * Marca una vulnerabilidad como resuelta.
   * Establece `resolved_date` solo con la parte de fecha (YYYY-MM-DD) porque
   * la columna en PostgreSQL es de tipo `DATE`, no `TIMESTAMP`.
   *
   * @param vulnerabilityId - UUID de la vulnerabilidad
   * @param notes           - Notas de remediación que explican cómo se resolvió
   */
  const resolveVulnerability = useCallback(async (vulnerabilityId: string, notes: string) => {
    try {
      logger.log('[ASSET] ✅ Resolviendo vulnerabilidad:', vulnerabilityId);

      const { error: updateError } = await supabase
        .from('asset_vulnerabilities')
        .update({
          status: 'resolved',
          resolved_date: new Date().toISOString().split('T')[0], // Solo fecha YYYY-MM-DD
          remediation_notes: notes
        })
        .eq('id', vulnerabilityId);

      if (updateError) throw updateError;

      logger.log('[ASSET] ✅ Vulnerabilidad resuelta');
      await loadVulnerabilities();
    } catch (err) {
      handleError(err, 'resolver vulnerabilidad');
      throw err;
    }
  }, [loadVulnerabilities, handleError]);

  /**
   * Elimina una vulnerabilidad. Registra el evento en el historial del activo
   * usando los datos de la vulnerabilidad antes de eliminarla (búsqueda local).
   *
   * @param vulnerabilityId - UUID de la vulnerabilidad a eliminar
   */
  const deleteVulnerability = useCallback(async (vulnerabilityId: string) => {
    try {
      logger.log('[ASSET] 🗑️ Eliminando vulnerabilidad:', vulnerabilityId);

      // Capturar datos antes de eliminar para el registro en historial
      const vuln = vulnerabilities.find(v => v.id === vulnerabilityId);

      const { error: deleteError } = await supabase
        .from('asset_vulnerabilities')
        .delete()
        .eq('id', vulnerabilityId);

      if (deleteError) throw deleteError;

      logger.log('[ASSET] ✅ Vulnerabilidad eliminada');

      // Registrar en historial del activo si se encontraron los datos
      if (vuln) {
        await logLifecycleEvent(
          vuln.assetId,
          'vulnerability_removed',
          `Vulnerabilidad eliminada: ${vuln.title}`,
          { previousValue: `${vuln.severity} - ${vuln.title}` }
        );
      }

      await loadVulnerabilities();
    } catch (err) {
      handleError(err, 'eliminar vulnerabilidad');
      throw err;
    }
  }, [vulnerabilities, loadVulnerabilities, handleError, logLifecycleEvent]);

  // =====================================================
  // CRUD AMENAZAS
  // =====================================================

  /**
   * Carga amenazas de la organización (clasificadas según ISO 27005).
   * Sin JOIN a usuarios — las amenazas no tienen responsable asignado.
   *
   * @param assetId - UUID del activo para filtrar (opcional — sin arg = todas)
   */
  const loadThreats = useCallback(async (assetId?: string) => {
    if (!user?.organization_id) return;

    try {
      console.log('[ASSET] 🔄 Consultando tabla asset_threats...');

      let query = supabase
        .from('asset_threats')
        .select('*')
        .eq('organization_id', user.organization_id)
        .order('created_at', { ascending: false });

      if (assetId) {
        query = query.eq('asset_id', assetId);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      const formattedThreats: AssetThreat[] = (data || []).map((t: Record<string, unknown>) => ({
        id: t.id as string,
        organizationId: t.organization_id as string,
        assetId: t.asset_id as string,
        threatType: t.threat_type as AssetThreat['threatType'],
        title: t.title as string,
        description: t.description as string | undefined,
        likelihood: t.likelihood as number,
        threatSource: t.threat_source as string | undefined,
        isActive: t.is_active as boolean,
        metadata: t.metadata as Record<string, unknown> | undefined,
        createdAt: t.created_at as string,
        updatedAt: t.updated_at as string,
        createdBy: t.created_by as string | undefined
      }));

      setThreats(formattedThreats);
      console.log(`[ASSET] ✅ Cargadas ${formattedThreats.length} amenazas`);
    } catch (err) {
      console.error('[ASSET] ❌ Error cargando amenazas:', err);
      handleError(err, 'cargar amenazas');
    }
  }, [user, handleError]);

  /**
   * Registra una nueva amenaza para un activo.
   * `isActive !== false` — si no se especifica, la amenaza se crea activa por defecto.
   *
   * @param threat - Datos de la amenaza sin campos generados por PostgreSQL
   */
  const createThreat = useCallback(async (
    threat: Omit<AssetThreat, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>
  ) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 📝 Creando amenaza...', threat);

      const { error: insertError } = await supabase
        .from('asset_threats')
        .insert({
          organization_id: user.organization_id,
          asset_id: threat.assetId,
          threat_type: threat.threatType,
          title: threat.title,
          description: threat.description,
          likelihood: threat.likelihood,
          threat_source: threat.threatSource,
          is_active: threat.isActive !== false, // Default activo si no se especifica
          metadata: threat.metadata || {},
          created_by: user.id
        });

      if (insertError) throw insertError;

      logger.log('[ASSET] ✅ Amenaza creada');

      // Registrar evento en historial del activo
      await logLifecycleEvent(
        threat.assetId,
        'threat_added',
        `Amenaza agregada: ${threat.title}`,
        { newValue: `${threat.threatType} - ${threat.title}` }
      );

      await loadThreats();
    } catch (err) {
      handleError(err, 'crear amenaza');
      throw err;
    }
  }, [user, loadThreats, handleError, logLifecycleEvent]);

  /**
   * Actualiza los campos de una amenaza existente.
   *
   * @param threatId - UUID de la amenaza
   * @param updates  - Campos a actualizar en camelCase
   */
  const updateThreat = useCallback(async (threatId: string, updates: Partial<AssetThreat>) => {
    try {
      logger.log('[ASSET] 🔄 Actualizando amenaza:', threatId, updates);

      const dbUpdates: Record<string, unknown> = {};
      if (updates.threatType !== undefined) dbUpdates.threat_type = updates.threatType;
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.likelihood !== undefined) dbUpdates.likelihood = updates.likelihood;
      if (updates.threatSource !== undefined) dbUpdates.threat_source = updates.threatSource;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;

      const { error: updateError } = await supabase
        .from('asset_threats')
        .update(dbUpdates)
        .eq('id', threatId);

      if (updateError) throw updateError;

      logger.log('[ASSET] ✅ Amenaza actualizada');
      await loadThreats();
    } catch (err) {
      handleError(err, 'actualizar amenaza');
      throw err;
    }
  }, [loadThreats, handleError]);

  /**
   * Elimina una amenaza. Registra el evento en el historial del activo.
   *
   * @param threatId - UUID de la amenaza a eliminar
   */
  const deleteThreat = useCallback(async (threatId: string) => {
    try {
      logger.log('[ASSET] 🗑️ Eliminando amenaza:', threatId);

      // Capturar datos antes de eliminar para el registro en historial
      const threat = threats.find(t => t.id === threatId);

      const { error: deleteError } = await supabase
        .from('asset_threats')
        .delete()
        .eq('id', threatId);

      if (deleteError) throw deleteError;

      logger.log('[ASSET] ✅ Amenaza eliminada');

      if (threat) {
        await logLifecycleEvent(
          threat.assetId,
          'threat_removed',
          `Amenaza eliminada: ${threat.title}`,
          { previousValue: `${threat.threatType} - ${threat.title}` }
        );
      }

      await loadThreats();
    } catch (err) {
      handleError(err, 'eliminar amenaza');
      throw err;
    }
  }, [threats, loadThreats, handleError, logLifecycleEvent]);

  // =====================================================
  // CRUD RIESGOS
  // =====================================================

  /**
   * Carga riesgos de la organización ordenados por nivel de riesgo descendente
   * (los más críticos primero). `riskLevel` = `impact_level × likelihood_level`,
   * calculado por PostgreSQL.
   *
   * @param assetId - UUID del activo para filtrar (opcional)
   */
  const loadRisks = useCallback(async (assetId?: string) => {
    if (!user?.organization_id) return;

    try {
      console.log('[ASSET] 🔄 Consultando tabla asset_risks...');

      let query = supabase
        .from('asset_risks')
        .select(`
          *,
          treatment_responsible:users!asset_risks_treatment_responsible_id_fkey (id, name, email, role, unit)
        `)
        .eq('organization_id', user.organization_id)
        .order('risk_level', { ascending: false }); // Críticos primero

      if (assetId) {
        query = query.eq('asset_id', assetId);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      const formattedRisks: AssetRisk[] = (data || []).map((r: Record<string, unknown>) => ({
        id: r.id as string,
        organizationId: r.organization_id as string,
        assetId: r.asset_id as string,
        vulnerabilityId: r.vulnerability_id as string | undefined,
        threatId: r.threat_id as string | undefined,
        title: r.title as string,
        description: r.description as string | undefined,
        impactLevel: r.impact_level as number,
        likelihoodLevel: r.likelihood_level as number,
        riskLevel: r.risk_level as number,         // Calculado por PostgreSQL: impacto × probabilidad
        riskCategory: r.risk_category as 'low' | 'medium' | 'high' | 'critical', // Calculado por trigger
        treatment: r.treatment as AssetRisk['treatment'],
        treatmentPlan: r.treatment_plan as string | undefined,
        treatmentResponsibleId: r.treatment_responsible_id as string | undefined,
        treatmentResponsible: formatUser(r.treatment_responsible as Record<string, unknown>),
        treatmentDueDate: r.treatment_due_date as string | undefined,
        residualRiskLevel: r.residual_risk_level as number | undefined,
        residualRiskCategory: r.residual_risk_category as 'low' | 'medium' | 'high' | 'critical' | undefined,
        status: r.status as AssetRisk['status'],
        lastReviewDate: r.last_review_date as string | undefined,
        nextReviewDate: r.next_review_date as string | undefined,
        metadata: r.metadata as Record<string, unknown> | undefined,
        createdAt: r.created_at as string,
        updatedAt: r.updated_at as string,
        createdBy: r.created_by as string | undefined
      }));

      setRisks(formattedRisks);
      console.log(`[ASSET] ✅ Cargados ${formattedRisks.length} riesgos`);
    } catch (err) {
      console.error('[ASSET] ❌ Error cargando riesgos:', err);
      handleError(err, 'cargar riesgos');
    }
  }, [user, handleError]);

  /**
   * Registra un nuevo riesgo. Omite `riskLevel` y `riskCategory` porque
   * PostgreSQL los calcula con un trigger al insertar.
   * Calcula el nivel localmente solo para el mensaje del evento en historial.
   *
   * @param risk - Datos del riesgo sin los campos calculados por PostgreSQL
   */
  const createRisk = useCallback(async (
    risk: Omit<AssetRisk, 'id' | 'createdAt' | 'updatedAt' | 'organizationId' | 'riskLevel' | 'riskCategory'>
  ) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 📝 Creando riesgo...', risk);

      const { error: insertError } = await supabase
        .from('asset_risks')
        .insert({
          organization_id: user.organization_id,
          asset_id: risk.assetId,
          vulnerability_id: risk.vulnerabilityId,
          threat_id: risk.threatId,
          title: risk.title,
          description: risk.description,
          impact_level: risk.impactLevel,
          likelihood_level: risk.likelihoodLevel,
          // riskLevel y riskCategory son calculados por trigger de PostgreSQL
          treatment: risk.treatment,
          treatment_plan: risk.treatmentPlan,
          treatment_responsible_id: risk.treatmentResponsibleId,
          treatment_due_date: risk.treatmentDueDate,
          status: risk.status || 'identified',
          metadata: risk.metadata || {},
          created_by: user.id
        });

      if (insertError) throw insertError;

      logger.log('[ASSET] ✅ Riesgo creado');

      // Calcular nivel localmente para el mensaje descriptivo del historial
      const riskLevel = risk.impactLevel * risk.likelihoodLevel;
      const riskCategory = riskLevel >= 20 ? 'Crítico' : riskLevel >= 12 ? 'Alto' : riskLevel >= 6 ? 'Medio' : 'Bajo';

      await logLifecycleEvent(
        risk.assetId,
        'risk_added',
        `Riesgo agregado: ${risk.title}`,
        { newValue: `${riskCategory} (${riskLevel}) - ${risk.title}` }
      );

      await loadRisks();
    } catch (err) {
      handleError(err, 'crear riesgo');
      throw err;
    }
  }, [user, loadRisks, handleError, logLifecycleEvent]);

  /**
   * Actualiza los campos de un riesgo existente.
   * Si se actualizan `impactLevel` o `likelihoodLevel`, PostgreSQL recalculará
   * automáticamente `riskLevel` y `riskCategory` mediante su trigger.
   *
   * @param riskId  - UUID del riesgo
   * @param updates - Campos a actualizar en camelCase
   */
  const updateRisk = useCallback(async (riskId: string, updates: Partial<AssetRisk>) => {
    try {
      logger.log('[ASSET] 🔄 Actualizando riesgo:', riskId, updates);

      const dbUpdates: Record<string, unknown> = {};
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.impactLevel !== undefined) dbUpdates.impact_level = updates.impactLevel;
      if (updates.likelihoodLevel !== undefined) dbUpdates.likelihood_level = updates.likelihoodLevel;
      if (updates.treatment !== undefined) dbUpdates.treatment = updates.treatment;
      if (updates.treatmentPlan !== undefined) dbUpdates.treatment_plan = updates.treatmentPlan;
      if (updates.treatmentResponsibleId !== undefined) dbUpdates.treatment_responsible_id = updates.treatmentResponsibleId;
      if (updates.treatmentDueDate !== undefined) dbUpdates.treatment_due_date = updates.treatmentDueDate;
      if (updates.residualRiskLevel !== undefined) dbUpdates.residual_risk_level = updates.residualRiskLevel;
      if (updates.residualRiskCategory !== undefined) dbUpdates.residual_risk_category = updates.residualRiskCategory;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if (updates.lastReviewDate !== undefined) dbUpdates.last_review_date = updates.lastReviewDate;
      if (updates.nextReviewDate !== undefined) dbUpdates.next_review_date = updates.nextReviewDate;

      const { error: updateError } = await supabase
        .from('asset_risks')
        .update(dbUpdates)
        .eq('id', riskId);

      if (updateError) throw updateError;

      logger.log('[ASSET] ✅ Riesgo actualizado');
      await loadRisks();
    } catch (err) {
      handleError(err, 'actualizar riesgo');
      throw err;
    }
  }, [loadRisks, handleError]);

  /**
   * Elimina un riesgo. Registra el evento en el historial del activo con la
   * categoría y nivel del riesgo eliminado para trazabilidad.
   *
   * @param riskId - UUID del riesgo a eliminar
   */
  const deleteRisk = useCallback(async (riskId: string) => {
    try {
      logger.log('[ASSET] 🗑️ Eliminando riesgo:', riskId);

      // Capturar datos antes de eliminar para el registro en historial
      const risk = risks.find(r => r.id === riskId);

      const { error: deleteError } = await supabase
        .from('asset_risks')
        .delete()
        .eq('id', riskId);

      if (deleteError) throw deleteError;

      logger.log('[ASSET] ✅ Riesgo eliminado');

      if (risk) {
        // Traducir la categoría para el mensaje en español del historial
        const riskCategoryLabel = risk.riskCategory === 'critical' ? 'Crítico' :
          risk.riskCategory === 'high' ? 'Alto' :
          risk.riskCategory === 'medium' ? 'Medio' : 'Bajo';

        await logLifecycleEvent(
          risk.assetId,
          'risk_removed',
          `Riesgo eliminado: ${risk.title}`,
          { previousValue: `${riskCategoryLabel} (${risk.riskLevel}) - ${risk.title}` }
        );
      }

      await loadRisks();
    } catch (err) {
      handleError(err, 'eliminar riesgo');
      throw err;
    }
  }, [risks, loadRisks, handleError, logLifecycleEvent]);

  // =====================================================
  // MAPEO DE CONTROLES
  // =====================================================

  /**
   * Carga los mapeos entre activos y controles ISO.
   * El JOIN con `iso_controls` incluye solo los campos necesarios para
   * mostrar el código, título y estado del control en la UI.
   *
   * @param assetId - UUID del activo para filtrar (opcional)
   */
  const loadControlMappings = useCallback(async (assetId?: string) => {
    if (!user?.organization_id) return;

    try {
      console.log('[ASSET] 🔄 Consultando tabla asset_control_mappings...');

      let query = supabase
        .from('asset_control_mappings')
        .select(`
          *,
          control:iso_controls (id, code, title, status, subcategory)
        `)
        .eq('organization_id', user.organization_id);

      if (assetId) {
        query = query.eq('asset_id', assetId);
      }

      const { data, error: queryError } = await query;

      if (queryError) throw queryError;

      const formattedMappings: AssetControlMapping[] = (data || []).map((m: Record<string, unknown>) => ({
        id: m.id as string,
        organizationId: m.organization_id as string,
        assetId: m.asset_id as string,
        controlId: m.control_id as string,
        coverageStatus: m.coverage_status as AssetControlMapping['coverageStatus'],
        effectivenessRating: m.effectiveness_rating as number | undefined,
        implementationNotes: m.implementation_notes as string | undefined,
        lastVerifiedDate: m.last_verified_date as string | undefined,
        verifiedBy: m.verified_by as string | undefined,
        createdAt: m.created_at as string,
        updatedAt: m.updated_at as string,
        createdBy: m.created_by as string | undefined
      }));

      setControlMappings(formattedMappings);
      console.log(`[ASSET] ✅ Cargados ${formattedMappings.length} mapeos de controles`);
    } catch (err) {
      console.error('[ASSET] ❌ Error cargando mapeos de controles:', err);
      handleError(err, 'cargar mapeo de controles');
    }
  }, [user, handleError]);

  /**
   * Vincula un control ISO a un activo de información.
   * La relación representa que el control proporciona cobertura de seguridad al activo.
   *
   * @param assetId       - UUID del activo
   * @param controlId     - UUID del control ISO
   * @param coverageStatus - Estado de cobertura: 'full', 'partial', 'planned', 'not_applicable'
   * @param notes         - Notas de implementación (opcional)
   */
  const linkControlToAsset = useCallback(async (
    assetId: string,
    controlId: string,
    coverageStatus: string,
    notes?: string
  ) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 🔗 Vinculando control a activo:', { assetId, controlId });

      const { error: insertError } = await supabase
        .from('asset_control_mappings')
        .insert({
          organization_id: user.organization_id,
          asset_id: assetId,
          control_id: controlId,
          coverage_status: coverageStatus,
          implementation_notes: notes,
          created_by: user.id
        });

      if (insertError) throw insertError;

      logger.log('[ASSET] ✅ Control vinculado a activo');

      await logLifecycleEvent(
        assetId,
        'control_linked',
        `Control vinculado: ${controlId}`,
        { newValue: `${coverageStatus} - ${controlId}` }
      );

      await loadControlMappings();
    } catch (err) {
      handleError(err, 'vincular control');
      throw err;
    }
  }, [user, loadControlMappings, handleError, logLifecycleEvent]);

  /**
   * Actualiza el estado de cobertura o efectividad de un mapeo control-activo.
   *
   * @param mappingId - UUID del mapeo
   * @param updates   - Campos a actualizar en camelCase
   */
  const updateControlMapping = useCallback(async (mappingId: string, updates: Partial<AssetControlMapping>) => {
    try {
      logger.log('[ASSET] 🔄 Actualizando mapeo:', mappingId, updates);

      const dbUpdates: Record<string, unknown> = {};
      if (updates.coverageStatus !== undefined) dbUpdates.coverage_status = updates.coverageStatus;
      if (updates.effectivenessRating !== undefined) dbUpdates.effectiveness_rating = updates.effectivenessRating;
      if (updates.implementationNotes !== undefined) dbUpdates.implementation_notes = updates.implementationNotes;
      if (updates.lastVerifiedDate !== undefined) dbUpdates.last_verified_date = updates.lastVerifiedDate;

      const { error: updateError } = await supabase
        .from('asset_control_mappings')
        .update(dbUpdates)
        .eq('id', mappingId);

      if (updateError) throw updateError;

      logger.log('[ASSET] ✅ Mapeo actualizado');
      await loadControlMappings();
    } catch (err) {
      handleError(err, 'actualizar mapeo');
      throw err;
    }
  }, [loadControlMappings, handleError]);

  /**
   * Desvincula un control de un activo. Registra el evento en el historial.
   *
   * @param mappingId - UUID del mapeo a eliminar
   */
  const unlinkControlFromAsset = useCallback(async (mappingId: string) => {
    try {
      logger.log('[ASSET] 🔗 Desvinculando control:', mappingId);

      // Capturar datos antes de eliminar para el registro en historial
      const mapping = controlMappings.find(m => m.id === mappingId);

      const { error: deleteError } = await supabase
        .from('asset_control_mappings')
        .delete()
        .eq('id', mappingId);

      if (deleteError) throw deleteError;

      logger.log('[ASSET] ✅ Control desvinculado');

      if (mapping) {
        await logLifecycleEvent(
          mapping.assetId,
          'control_unlinked',
          `Control desvinculado: ${mapping.controlId}`,
          { previousValue: mapping.controlId }
        );
      }

      await loadControlMappings();
    } catch (err) {
      handleError(err, 'desvincular control');
      throw err;
    }
  }, [controlMappings, loadControlMappings, handleError, logLifecycleEvent]);

  // =====================================================
  // CICLO DE VIDA
  // =====================================================

  /**
   * Carga el historial de eventos de ciclo de vida de un activo específico.
   * A diferencia de otras funciones `load*`, no actualiza estado global —
   * retorna los eventos directamente para uso en paneles de detalle.
   *
   * Ordenado por `performed_at` descendente (eventos más recientes primero).
   *
   * @param assetId - UUID del activo cuyo historial se quiere cargar
   * @returns Array de eventos, o `[]` si hay error o no hay eventos
   */
  const loadLifecycleEvents = useCallback(async (assetId: string): Promise<AssetLifecycleEvent[]> => {
    if (!user?.organization_id) return [];

    try {
      logger.log('[ASSET] 🔄 Cargando eventos del ciclo de vida:', assetId);

      const { data, error: queryError } = await supabase
        .from('asset_lifecycle_events')
        .select(`
          *,
          performed_by:users!asset_lifecycle_events_performed_by_id_fkey (id, name, email, role, unit)
        `)
        .eq('asset_id', assetId)
        .order('performed_at', { ascending: false });

      if (queryError) throw queryError;

      const events: AssetLifecycleEvent[] = (data || []).map((e: Record<string, unknown>) => ({
        id: e.id as string,
        organizationId: e.organization_id as string,
        assetId: e.asset_id as string,
        eventType: e.event_type as AssetLifecycleEvent['eventType'],
        previousStatus: e.previous_status as AssetLifecycleStatus | undefined,
        newStatus: e.new_status as AssetLifecycleStatus | undefined,
        previousValue: e.previous_value as string | undefined,
        newValue: e.new_value as string | undefined,
        description: e.description as string | undefined,
        notes: e.notes as string | undefined,
        performedById: e.performed_by_id as string,
        performedBy: formatUser(e.performed_by as Record<string, unknown>),
        performedAt: e.performed_at as string
      }));

      logger.log(`[ASSET] ✅ Cargados ${events.length} eventos`);
      return events;
    } catch (err) {
      handleError(err, 'cargar eventos del ciclo de vida');
      return [];
    }
  }, [user, handleError]);

  /**
   * Crea un evento de ciclo de vida explícito para un activo.
   * Para la mayoría de los casos, los eventos se crean automáticamente vía
   * `logLifecycleEvent()`. Esta función es para eventos manuales desde la UI.
   *
   * @param assetId   - UUID del activo
   * @param eventType - Tipo de evento
   * @param description - Descripción del evento (opcional)
   * @param notes     - Notas adicionales (opcional)
   */
  const createLifecycleEvent = useCallback(async (
    assetId: string,
    eventType: AssetLifecycleEvent['eventType'],
    description?: string,
    notes?: string
  ) => {
    if (!user?.organization_id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 🔄 Creando evento del ciclo de vida:', { assetId, eventType });

      const { error: insertError } = await supabase
        .from('asset_lifecycle_events')
        .insert({
          organization_id: user.organization_id,
          asset_id: assetId,
          event_type: eventType,
          description: description || null,
          notes: notes || null,
          performed_by_id: user.id,
          performed_at: new Date().toISOString()
        });

      if (insertError) throw insertError;

      logger.log('[ASSET] ✅ Evento del ciclo de vida creado');
    } catch (err) {
      handleError(err, 'crear evento del ciclo de vida');
      throw err;
    }
  }, [user, handleError]);

  /**
   * Cambia el estado del ciclo de vida de un activo.
   * Delega a `updateAsset()` para el cambio en BD.
   * El trigger `log_asset_lifecycle_event` de PostgreSQL registra automáticamente
   * el cambio de estado en `asset_lifecycle_events` con previousStatus y newStatus.
   *
   * @param assetId   - UUID del activo
   * @param newStatus - Nuevo estado del ciclo de vida
   * @param notes     - Notas justificativas del cambio (opcional)
   */
  const changeAssetStatus = useCallback(async (assetId: string, newStatus: AssetLifecycleStatus, notes?: string) => {
    if (!user?.id) throw new Error('Usuario no autenticado');

    try {
      logger.log('[ASSET] 🔄 Cambiando estado del activo:', { assetId, newStatus });

      // El trigger de PostgreSQL `log_asset_lifecycle_event` detecta el cambio de status
      // y crea automáticamente el evento en asset_lifecycle_events con prev/new status
      await updateAsset(assetId, { status: newStatus });

      logger.log('[ASSET] ✅ Estado del activo actualizado');
    } catch (err) {
      handleError(err, 'cambiar estado del activo');
      throw err;
    }
  }, [user, updateAsset, handleError]);

  // =====================================================
  // GESTIÓN DE CAMBIOS (ISO 27002 A.8.32)
  // =====================================================

  /**
   * Transforma un registro de cambio de la BD (snake_case + any) al tipo de la aplicación.
   * El parámetro es `any` porque la tabla `asset_change_management` no está en
   * `database.types.ts` (fue agregada después de la generación del tipo).
   * Los JOINs de usuarios se incluyen como campos directos en el objeto.
   *
   * @param dbChange - Fila de la tabla con campos snake_case + usuarios como objetos anidados
   * @returns `AssetChangeManagement` con todos los campos en camelCase
   */
  const transformChangeFromDB = (dbChange: any): AssetChangeManagement => {
    return {
      id: dbChange.id,
      organizationId: dbChange.organization_id,
      assetId: dbChange.asset_id,
      changeType: dbChange.change_type,
      title: dbChange.title,
      description: dbChange.description,
      versionFrom: dbChange.version_from,
      versionTo: dbChange.version_to,
      justification: dbChange.justification,
      impactAnalysis: dbChange.impact_analysis,
      riskAssessment: dbChange.risk_assessment,
      status: dbChange.status,
      requestedById: dbChange.requested_by_id,
      requestedBy: dbChange.requestedBy,
      approvedById: dbChange.approved_by_id,
      approvedBy: dbChange.approvedBy,
      approvedByExternalName: dbChange.approved_by_external_name,
      approvedAt: dbChange.approved_at,
      scheduledStart: dbChange.scheduled_start,
      scheduledEnd: dbChange.scheduled_end,
      requiresDowntime: dbChange.requires_downtime,
      downtimeDurationMinutes: dbChange.downtime_duration_minutes,
      implementedById: dbChange.implemented_by_id,
      implementedBy: dbChange.implementedBy,
      implementedAt: dbChange.implemented_at,
      actualStart: dbChange.actual_start,
      actualEnd: dbChange.actual_end,
      rollbackPlan: dbChange.rollback_plan,
      rollbackPerformed: dbChange.rollback_performed,
      rollbackReason: dbChange.rollback_reason,
      testingPlan: dbChange.testing_plan,
      testingResults: dbChange.testing_results,
      verifiedById: dbChange.verified_by_id,
      verifiedBy: dbChange.verifiedBy,
      verifiedAt: dbChange.verified_at,
      changeDocumentationUrl: dbChange.change_documentation_url,
      approvalDocumentUrl: dbChange.approval_document_url,
      notes: dbChange.notes,
      lessonsLearned: dbChange.lessons_learned,
      createdAt: dbChange.created_at,
      updatedAt: dbChange.updated_at,
      createdById: dbChange.created_by_id,
      updatedById: dbChange.updated_by_id
    };
  };

  /**
   * Carga el historial de cambios de gestión de un activo de software.
   * Solo aplica a activos con `assetType === 'software'` — el componente
   * es responsable de no llamar esta función para otros tipos.
   *
   * @param assetId - UUID del activo de software
   * @returns Array de cambios ordenados por fecha descendente, o `[]` si hay error
   */
  const loadAssetChanges = useCallback(async (assetId: string): Promise<AssetChangeManagement[]> => {
    if (!user?.organization_id) return [];

    try {
      logger.log('[ASSET] 📝 Cargando cambios del activo:', assetId);

      const { data, error } = await supabase
        .from('asset_change_management')
        .select(`
          *,
          requestedBy:requested_by_id(id, name, email),
          approvedBy:approved_by_id(id, name, email),
          implementedBy:implemented_by_id(id, name, email),
          verifiedBy:verified_by_id(id, name, email)
        `)
        .eq('asset_id', assetId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      logger.log('[ASSET] ✅ Cambios cargados:', data?.length || 0);
      return (data || []).map(transformChangeFromDB);
    } catch (err) {
      logger.error('[ASSET] ❌ Error al cargar cambios:', err);
      return [];
    }
  }, [user]);

  /**
   * Registra un nuevo cambio de software en el sistema de gestión de cambios.
   * El mapeo camelCase → snake_case es explícito y exhaustivo para garantizar
   * que todos los campos del tipo lleguen correctamente a la BD.
   *
   * @param change - Datos del cambio sin campos generados por PostgreSQL
   * @returns El cambio creado con su ID y timestamps de la BD
   */
  const createChange = useCallback(async (
    change: Omit<AssetChangeManagement, 'id' | 'createdAt' | 'updatedAt' | 'organizationId'>
  ): Promise<AssetChangeManagement> => {
    if (!user?.organization_id) throw new Error('No organization');

    try {
      logger.log('[ASSET] 📝 Creando cambio:', change.title);

      const { data, error } = await supabase
        .from('asset_change_management')
        .insert({
          asset_id: change.assetId,
          organization_id: user.organization_id,

          // Información del cambio
          change_type: change.changeType,
          title: change.title,
          description: change.description,

          // Versiones de software
          version_from: change.versionFrom,
          version_to: change.versionTo,

          // Justificación y análisis de impacto/riesgo
          justification: change.justification,
          impact_analysis: change.impactAnalysis,
          risk_assessment: change.riskAssessment,

          // Estado y aprobación
          status: change.status,
          requested_by_id: change.requestedById,
          approved_by_id: change.approvedById,
          approved_by_external_name: change.approvedByExternalName,
          approved_at: change.approvedAt,

          // Planificación temporal
          scheduled_start: change.scheduledStart,
          scheduled_end: change.scheduledEnd,
          requires_downtime: change.requiresDowntime,
          downtime_duration_minutes: change.downtimeDurationMinutes,

          // Ejecución
          implemented_by_id: change.implementedById,
          implemented_at: change.implementedAt,
          actual_start: change.actualStart,
          actual_end: change.actualEnd,

          // Plan de rollback
          rollback_plan: change.rollbackPlan,
          rollback_performed: change.rollbackPerformed,
          rollback_reason: change.rollbackReason,

          // Pruebas y verificación de efectividad
          testing_plan: change.testingPlan,
          testing_results: change.testingResults,
          verified_by_id: change.verifiedById,
          verified_at: change.verifiedAt,

          // Documentación
          change_documentation_url: change.changeDocumentationUrl,
          approval_document_url: change.approvalDocumentUrl,

          // Notas y aprendizajes
          notes: change.notes,
          lessons_learned: change.lessonsLearned,

          // Auditoría
          created_by_id: user.id,
          updated_by_id: user.id
        })
        .select(`
          *,
          requestedBy:requested_by_id(id, name, email),
          approvedBy:approved_by_id(id, name, email),
          implementedBy:implemented_by_id(id, name, email),
          verifiedBy:verified_by_id(id, name, email)
        `)
        .single();

      if (error) throw error;

      logger.log('[ASSET] ✅ Cambio creado:', data.id);
      return transformChangeFromDB(data);
    } catch (err) {
      handleError(err, 'crear cambio');
      throw err;
    }
  }, [user, handleError]);

  /**
   * Actualiza los campos de un cambio existente.
   * El mapeo camelCase → snake_case es exhaustivo con verificación `!== undefined`.
   * Siempre incluye `updated_by_id` para el trail de auditoría.
   *
   * @param changeId - UUID del cambio
   * @param updates  - Campos a actualizar en camelCase
   * @returns El cambio actualizado
   */
  const updateChange = useCallback(async (
    changeId: string,
    updates: Partial<AssetChangeManagement>
  ): Promise<AssetChangeManagement> => {
    if (!user) throw new Error('No user');

    try {
      logger.log('[ASSET] 🔄 Actualizando cambio:', changeId);

      const updateData: Record<string, unknown> = {};

      if (updates.changeType !== undefined) updateData.change_type = updates.changeType;
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.versionFrom !== undefined) updateData.version_from = updates.versionFrom;
      if (updates.versionTo !== undefined) updateData.version_to = updates.versionTo;
      if (updates.justification !== undefined) updateData.justification = updates.justification;
      if (updates.impactAnalysis !== undefined) updateData.impact_analysis = updates.impactAnalysis;
      if (updates.riskAssessment !== undefined) updateData.risk_assessment = updates.riskAssessment;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.requestedById !== undefined) updateData.requested_by_id = updates.requestedById;
      if (updates.approvedById !== undefined) updateData.approved_by_id = updates.approvedById;
      if (updates.approvedByExternalName !== undefined) updateData.approved_by_external_name = updates.approvedByExternalName;
      if (updates.approvedAt !== undefined) updateData.approved_at = updates.approvedAt;
      if (updates.scheduledStart !== undefined) updateData.scheduled_start = updates.scheduledStart;
      if (updates.scheduledEnd !== undefined) updateData.scheduled_end = updates.scheduledEnd;
      if (updates.requiresDowntime !== undefined) updateData.requires_downtime = updates.requiresDowntime;
      if (updates.downtimeDurationMinutes !== undefined) updateData.downtime_duration_minutes = updates.downtimeDurationMinutes;
      if (updates.implementedById !== undefined) updateData.implemented_by_id = updates.implementedById;
      if (updates.implementedAt !== undefined) updateData.implemented_at = updates.implementedAt;
      if (updates.actualStart !== undefined) updateData.actual_start = updates.actualStart;
      if (updates.actualEnd !== undefined) updateData.actual_end = updates.actualEnd;
      if (updates.rollbackPlan !== undefined) updateData.rollback_plan = updates.rollbackPlan;
      if (updates.rollbackPerformed !== undefined) updateData.rollback_performed = updates.rollbackPerformed;
      if (updates.rollbackReason !== undefined) updateData.rollback_reason = updates.rollbackReason;
      if (updates.testingPlan !== undefined) updateData.testing_plan = updates.testingPlan;
      if (updates.testingResults !== undefined) updateData.testing_results = updates.testingResults;
      if (updates.verifiedById !== undefined) updateData.verified_by_id = updates.verifiedById;
      if (updates.verifiedAt !== undefined) updateData.verified_at = updates.verifiedAt;
      if (updates.changeDocumentationUrl !== undefined) updateData.change_documentation_url = updates.changeDocumentationUrl;
      if (updates.approvalDocumentUrl !== undefined) updateData.approval_document_url = updates.approvalDocumentUrl;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.lessonsLearned !== undefined) updateData.lessons_learned = updates.lessonsLearned;

      // Siempre incluir para el trail de auditoría
      updateData.updated_by_id = user.id;

      const { data, error } = await supabase
        .from('asset_change_management')
        .update(updateData)
        .eq('id', changeId)
        .select()
        .single();

      if (error) throw error;

      logger.log('[ASSET] ✅ Cambio actualizado');
      return data as AssetChangeManagement;
    } catch (err) {
      handleError(err, 'actualizar cambio');
      throw err;
    }
  }, [user, handleError]);

  /**
   * Aprueba un cambio de software.
   * Shortcut sobre `updateChange()` que establece estado, aprobador y timestamp.
   *
   * @param changeId - UUID del cambio a aprobar
   * @param notes    - Notas de aprobación (opcional)
   * @returns El cambio con estado 'approved'
   */
  const approveChange = useCallback(async (
    changeId: string,
    notes?: string
  ): Promise<AssetChangeManagement> => {
    if (!user) throw new Error('No user');

    logger.log('[ASSET] ✅ Aprobando cambio:', changeId);

    return updateChange(changeId, {
      status: 'approved',
      approvedById: user.id,
      approvedAt: new Date().toISOString(),
      notes: notes
    });
  }, [user, updateChange]);

  /**
   * Marca un cambio como completado con los resultados reales de implementación.
   * Los `results` contienen las fechas reales vs. las planificadas y resultados de testing.
   *
   * @param changeId - UUID del cambio
   * @param results  - Datos de implementación real (fechas, resultados de pruebas, notas)
   * @returns El cambio con estado 'completed'
   */
  const completeChange = useCallback(async (
    changeId: string,
    results: {
      actualStart?: string;
      actualEnd?: string;
      testingResults?: string;
      notes?: string;
    }
  ): Promise<AssetChangeManagement> => {
    if (!user) throw new Error('No user');

    logger.log('[ASSET] ✅ Completando cambio:', changeId);

    return updateChange(changeId, {
      status: 'completed',
      implementedById: user.id,
      implementedAt: new Date().toISOString(),
      ...results // Spread para incluir actualStart, actualEnd, testingResults, notes
    });
  }, [user, updateChange]);

  /**
   * Verifica la efectividad de un cambio completado (→ 'verified').
   *
   * @param changeId - UUID del cambio a verificar
   * @param notes    - Evaluación de efectividad del cambio (opcional)
   * @returns El cambio con estado 'verified'
   */
  const verifyChange = useCallback(async (
    changeId: string,
    notes?: string
  ): Promise<AssetChangeManagement> => {
    if (!user) throw new Error('No user');

    logger.log('[ASSET] ✅ Verificando cambio:', changeId);

    return updateChange(changeId, {
      status: 'verified',
      verifiedById: user.id,
      verifiedAt: new Date().toISOString(),
      notes: notes
    });
  }, [user, updateChange]);

  /**
   * Revierte un cambio ejecutado (→ 'rolled_back').
   * Se activa cuando la implementación falló o causó problemas en producción.
   *
   * @param changeId - UUID del cambio a revertir
   * @param reason   - Motivo del rollback (requerido — documentación obligatoria)
   * @returns El cambio con estado 'rolled_back'
   */
  const rollbackChange = useCallback(async (
    changeId: string,
    reason: string
  ): Promise<AssetChangeManagement> => {
    logger.log('[ASSET] ⚠️ Revirtiendo cambio:', changeId);

    return updateChange(changeId, {
      status: 'rolled_back',
      rollbackPerformed: true,
      rollbackReason: reason,
      implementedAt: new Date().toISOString() // Timestamp del rollback
    });
  }, [updateChange]);

  // =====================================================
  // ACTIVOS FILTRADOS
  // =====================================================

  /**
   * Lista de activos filtrada según los criterios activos en `filter` y `searchQuery`.
   * Calculada con `useMemo` — solo se recalcula cuando cambia `assets`, `vulnerabilities`,
   * `risks`, `searchQuery` o `filter`.
   *
   * ## Lógica de filtrado
   * - Arrays vacíos en `filter.*` = sin restricción para esa dimensión
   * - `null` en booleanos = sin restricción para esa condición
   * - `searchQuery` busca en nombre, código, descripción y departamento (case-insensitive)
   * - Los filtros se aplican con AND (todos los criterios deben cumplirse)
   *
   * Los filtros `hasOpenVulnerabilities` y `hasHighRisks` requieren los Sets de
   * vulnerabilidades y riesgos para cruzar información entre entidades.
   */
  const filteredAssets = useMemo(() => {
    let result = [...assets];

    // ── Búsqueda por texto libre ────────────────────────────────────────────
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(asset =>
        asset.name.toLowerCase().includes(query) ||
        asset.code.toLowerCase().includes(query) ||
        asset.description?.toLowerCase().includes(query) ||
        asset.department?.toLowerCase().includes(query)
      );
    }

    // ── Filtros por dimensión ───────────────────────────────────────────────
    if (filter.assetType.length > 0) {
      result = result.filter(asset => filter.assetType.includes(asset.assetType));
    }

    if (filter.classification.length > 0) {
      result = result.filter(asset => filter.classification.includes(asset.classification));
    }

    if (filter.criticality.length > 0) {
      result = result.filter(asset => filter.criticality.includes(asset.criticality));
    }

    if (filter.status.length > 0) {
      result = result.filter(asset => filter.status.includes(asset.status));
    }

    if (filter.owner.length > 0) {
      result = result.filter(asset => asset.ownerId && filter.owner.includes(asset.ownerId));
    }

    if (filter.department.length > 0) {
      result = result.filter(asset => asset.department && filter.department.includes(asset.department));
    }

    // ── Filtros cruzados (requieren Sets de otras entidades) ────────────────
    if (filter.hasOpenVulnerabilities !== undefined) {
      // Set de assetIds con al menos una vulnerabilidad abierta
      const assetsWithOpenVulns = new Set(
        vulnerabilities.filter(v => v.status === 'open').map(v => v.assetId)
      );
      result = result.filter(asset =>
        filter.hasOpenVulnerabilities === true
          ? assetsWithOpenVulns.has(asset.id)
          : !assetsWithOpenVulns.has(asset.id)
      );
    }

    if (filter.hasHighRisks !== undefined) {
      // Set de assetIds con al menos un riesgo alto o crítico
      const assetsWithHighRisks = new Set(
        risks.filter(r => r.riskCategory === 'high' || r.riskCategory === 'critical').map(r => r.assetId)
      );
      result = result.filter(asset =>
        filter.hasHighRisks === true
          ? assetsWithHighRisks.has(asset.id)
          : !assetsWithHighRisks.has(asset.id)
      );
    }

    // ── Filtro de revisión pendiente ────────────────────────────────────────
    if (filter.needsReview === true) {
      const today = new Date().toISOString().split('T')[0];
      // Activos cuya próxima revisión está en el pasado (vencida o hoy)
      result = result.filter(asset =>
        asset.nextReviewDate && asset.nextReviewDate <= today
      );
    }

    // ── Filtro por rango de fechas (fecha de creación) ──────────────────────
    if (filter.dateRange.start) {
      result = result.filter(asset => asset.createdAt >= filter.dateRange.start);
    }
    if (filter.dateRange.end) {
      result = result.filter(asset => asset.createdAt <= filter.dateRange.end);
    }

    return result;
  }, [assets, vulnerabilities, risks, searchQuery, filter]);

  // =====================================================
  // MÉTRICAS
  // =====================================================

  /**
   * Métricas del inventario de activos calculadas en tiempo real.
   * Solo se recalcula cuando cambia `assets`, `vulnerabilities`, `risks` o `controlMappings`.
   *
   * ## Estructura de las métricas
   * - **Totales y por dimensión**: conteos simples con `filter()`
   * - **Alertas**: condiciones de negocio críticas (sin DRP, sin propietario, revisión vencida)
   * - **Tendencias**: actividad de los últimos 30 días para dashboards ejecutivos
   * - **Cobertura**: análisis de qué activos tienen controles ISO asociados
   *
   * ## Cálculos de Sets para performance
   * Los Sets (`assetIdsWithControls`, `assetIdsWithOpenVulns`, etc.) se calculan
   * una vez al inicio para evitar búsquedas O(n) repetidas en las métricas de alerta.
   *
   * ## Alerta: activos confidenciales sin controles
   * Los activos con clasificación `confidential` o `restricted` sin ningún control
   * ISO vinculado representan un riesgo de cumplimiento crítico (ISO 27001 A.5.12).
   *
   * ## Alerta: activos críticos sin DRP
   * Los activos con criticidad `critical` o `high` sin Plan de Recuperación ante
   * Desastres configurado en `metadata.drp` requieren acción inmediata (ISO 22301).
   */
  const metrics = useMemo((): AssetMetrics => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    const today = now.toISOString().split('T')[0];

    // Pre-calcular Sets para O(1) lookup en métricas de alerta
    const assetIdsWithControls = new Set(controlMappings.map(m => m.assetId));
    const assetIdsWithOpenVulns = new Set(
      vulnerabilities.filter(v => v.status === 'open').map(v => v.assetId)
    );
    const criticalAssetIds = new Set(
      assets.filter(a => a.criticality === 'critical').map(a => a.id)
    );
    const assetsWithHighRisks = new Set(
      risks.filter(r => r.riskCategory === 'high' || r.riskCategory === 'critical').map(r => r.assetId)
    );
    const criticalAssetsWithHighRisks = [...criticalAssetIds].filter(id => assetsWithHighRisks.has(id)).length;

    return {
      // ── Totales generales ──────────────────────────────────────────────────
      totalAssets: assets.length,
      activeAssets: assets.filter(a => a.status === 'active').length,
      retiredAssets: assets.filter(a => a.status === 'retired').length,

      // ── Distribución por tipo ──────────────────────────────────────────────
      byType: {
        hardware: assets.filter(a => a.assetType === 'hardware').length,
        software: assets.filter(a => a.assetType === 'software').length,
        data: assets.filter(a => a.assetType === 'data').length,
        service: assets.filter(a => a.assetType === 'service').length,
        people: assets.filter(a => a.assetType === 'people').length
      },

      // ── Distribución por clasificación de seguridad ────────────────────────
      byClassification: {
        public: assets.filter(a => a.classification === 'public').length,
        internal: assets.filter(a => a.classification === 'internal').length,
        confidential: assets.filter(a => a.classification === 'confidential').length,
        restricted: assets.filter(a => a.classification === 'restricted').length
      },

      // ── Distribución por criticidad CIA ───────────────────────────────────
      byCriticality: {
        low: assets.filter(a => a.criticality === 'low').length,
        medium: assets.filter(a => a.criticality === 'medium').length,
        high: assets.filter(a => a.criticality === 'high').length,
        critical: assets.filter(a => a.criticality === 'critical').length
      },

      // ── Distribución por estado del ciclo de vida ──────────────────────────
      byStatus: {
        proposed: assets.filter(a => a.status === 'proposed').length,
        approved: assets.filter(a => a.status === 'approved').length,
        active: assets.filter(a => a.status === 'active').length,
        maintenance: assets.filter(a => a.status === 'maintenance').length,
        obsolete: assets.filter(a => a.status === 'obsolete').length,
        retired: assets.filter(a => a.status === 'retired').length
      },

      // ── Vulnerabilidades ──────────────────────────────────────────────────
      totalVulnerabilities: vulnerabilities.length,
      openVulnerabilities: vulnerabilities.filter(v => v.status === 'open').length,
      // Vulnerabilidades críticas no resueltas (abiertas, en remediación o aceptadas)
      criticalVulnerabilities: vulnerabilities.filter(v => v.severity === 'critical' && v.status !== 'resolved').length,
      vulnerabilitiesBySeverity: {
        critical: vulnerabilities.filter(v => v.severity === 'critical').length,
        high: vulnerabilities.filter(v => v.severity === 'high').length,
        medium: vulnerabilities.filter(v => v.severity === 'medium').length,
        low: vulnerabilities.filter(v => v.severity === 'low').length
      },

      // ── Riesgos ───────────────────────────────────────────────────────────
      totalRisks: risks.length,
      highRisks: risks.filter(r => r.riskCategory === 'high' || r.riskCategory === 'critical').length,
      // Riesgos sin plan de tratamiento definido (excluyendo los cerrados)
      risksWithoutTreatment: risks.filter(r => !r.treatmentPlan && r.status !== 'closed').length,
      risksByCategory: {
        low: risks.filter(r => r.riskCategory === 'low').length,
        medium: risks.filter(r => r.riskCategory === 'medium').length,
        high: risks.filter(r => r.riskCategory === 'high').length,
        critical: risks.filter(r => r.riskCategory === 'critical').length
      },
      risksByTreatment: {
        mitigate: risks.filter(r => r.treatment === 'mitigate').length,
        accept: risks.filter(r => r.treatment === 'accept').length,
        transfer: risks.filter(r => r.treatment === 'transfer').length,
        avoid: risks.filter(r => r.treatment === 'avoid').length
      },

      // ── Cobertura de controles ISO ────────────────────────────────────────
      assetsWithControls: assetIdsWithControls.size,
      assetsWithoutControls: assets.length - assetIdsWithControls.size,
      // Promedio de controles por activo (0 si no hay activos)
      averageControlsPerAsset: assets.length > 0 ? controlMappings.length / assets.length : 0,

      // ── Alertas de cumplimiento ───────────────────────────────────────────
      assetsWithoutOwner: assets.filter(a => !a.ownerId).length,
      assetsNeedingReview: assets.filter(a => a.nextReviewDate && a.nextReviewDate <= today).length,
      assetsWithOpenVulnerabilities: assetIdsWithOpenVulns.size,
      criticalAssetsWithHighRisks,

      // ── Alertas adicionales ISO 27001/27005 ───────────────────────────────
      // Activos críticos/altos sin DRP — recuperación ante desastres obligatoria (ISO 22301)
      assetsWithoutDRP: assets.filter(a =>
        (a.criticality === 'critical' || a.criticality === 'high') &&
        (!a.drpPlan || !a.drpPlan.recoveryProcedures || a.drpPlan.recoveryProcedures.trim() === '')
      ).length,
      // Activos obsoletos pendientes de retiro formal
      obsoleteAssets: assets.filter(a => a.status === 'obsolete').length,
      // Cantidad de activos únicos con al menos una vuln crítica abierta
      assetsWithCriticalVulnerabilities: new Set(
        vulnerabilities.filter(v => v.severity === 'critical' && v.status === 'open').map(v => v.assetId)
      ).size,
      // Activos confidenciales/restringidos sin controles — riesgo de cumplimiento crítico
      confidentialAssetsWithoutControls: assets.filter(a =>
        (a.classification === 'confidential' || a.classification === 'restricted') &&
        !assetIdsWithControls.has(a.id)
      ).length,

      // ── Tendencias (últimos 30 días) ──────────────────────────────────────
      assetsCreatedLast30Days: assets.filter(a => a.createdAt >= thirtyDaysAgo).length,
      assetsRetiredLast30Days: assets.filter(a => a.status === 'retired' && a.updatedAt >= thirtyDaysAgo).length,
      vulnerabilitiesResolvedLast30Days: vulnerabilities.filter(v =>
        v.status === 'resolved' && v.resolvedDate && v.resolvedDate >= thirtyDaysAgo.split('T')[0]
      ).length
    };
  }, [assets, vulnerabilities, risks, controlMappings]);

  // =====================================================
  // REFRESH DATA
  // =====================================================

  /**
   * Recarga todos los módulos del inventario de activos secuencialmente.
   * Cada módulo se carga por separado (no en paralelo) para facilitar el diagnóstico
   * de errores específicos por tabla — si una tabla no existe, el log lo indica.
   */
  const refreshData = useCallback(async () => {
    if (!user?.organization_id) {
      logger.warn('[ASSET] ⚠️ No hay organization_id, saltando carga de datos');
      return;
    }

    setIsLoading(true);
    logger.log('[ASSET] 🔄 Iniciando refreshData...');

    try {
      logger.log('[ASSET] 📦 Cargando activos...');
      await loadAssets();

      logger.log('[ASSET] 🔓 Cargando vulnerabilidades...');
      await loadVulnerabilities();

      logger.log('[ASSET] ⚡ Cargando amenazas...');
      await loadThreats();

      logger.log('[ASSET] 📊 Cargando riesgos...');
      await loadRisks();

      logger.log('[ASSET] 🔗 Cargando mapeos de controles...');
      await loadControlMappings();

      logger.log('[ASSET] ✅ refreshData completado');
    } catch (err) {
      logger.error('[ASSET] ❌ Error en refreshData:', err);
      console.error('Error refrescando datos de activos:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, loadAssets, loadVulnerabilities, loadThreats, loadRisks, loadControlMappings]);

  // =====================================================
  // EFECTOS
  // =====================================================

  /**
   * Carga inicial de todos los módulos de activos al autenticarse el usuario.
   * Carga secuencialmente (no en paralelo) para:
   *   1. Diagnosticar errores por tabla individual si alguna migración falta
   *   2. Evitar saturar la conexión con 5 queries simultáneas en el montaje
   *
   * La condición `dbUserReady` garantiza que `user.organization_id` proviene
   * de `public.users` (no solo del JWT) antes de comenzar la carga.
   */
  useEffect(() => {
    const loadInitialData = async () => {
      if (!user?.organization_id) {
        console.log('[ASSET] ⚠️ No hay usuario u organization_id');
        return;
      }

      if (!dbUserReady) {
        console.log('[ASSET] ⏳ Esperando sync con BD...');
        return;
      }

      console.log('[ASSET] 🚀 INICIANDO CARGA DE DATOS - Organization:', user.organization_id);
      setIsLoading(true);

      try {
        // Carga secuencial — facilita diagnóstico de errores por tabla
        console.log('[ASSET] 📦 1/5 Cargando activos...');
        await loadAssets();
        console.log('[ASSET] ✅ Activos cargados');

        console.log('[ASSET] 🔓 2/5 Cargando vulnerabilidades...');
        await loadVulnerabilities();
        console.log('[ASSET] ✅ Vulnerabilidades cargadas');

        console.log('[ASSET] ⚡ 3/5 Cargando amenazas...');
        await loadThreats();
        console.log('[ASSET] ✅ Amenazas cargadas');

        console.log('[ASSET] 📊 4/5 Cargando riesgos...');
        await loadRisks();
        console.log('[ASSET] ✅ Riesgos cargados');

        console.log('[ASSET] 🔗 5/5 Cargando mapeos de controles...');
        await loadControlMappings();
        console.log('[ASSET] ✅ Mapeos cargados');

        console.log('[ASSET] 🎉 CARGA COMPLETADA EXITOSAMENTE');
      } catch (err) {
        console.error('[ASSET] ❌ Error durante la carga inicial:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [user?.organization_id, dbUserReady, loadAssets, loadVulnerabilities, loadThreats, loadRisks, loadControlMappings]);

  // =====================================================
  // VALOR DEL CONTEXTO
  // =====================================================

  const value: AssetContextType = {
    // Estados
    assets,
    vulnerabilities,
    threats,
    risks,
    controlMappings,
    isLoading,
    error,
    filter,
    searchQuery,

    // Utilidades
    clearError,
    refreshData,
    setFilter,
    resetFilter,
    setSearchQuery,

    // Activos filtrados
    filteredAssets,

    // Métricas
    metrics,

    // CRUD Activos
    loadAssets,
    createAsset,
    updateAsset,
    deleteAsset,
    getAssetById,

    // CRUD Vulnerabilidades
    loadVulnerabilities,
    createVulnerability,
    updateVulnerability,
    resolveVulnerability,
    deleteVulnerability,

    // CRUD Amenazas
    loadThreats,
    createThreat,
    updateThreat,
    deleteThreat,

    // CRUD Riesgos
    loadRisks,
    createRisk,
    updateRisk,
    deleteRisk,

    // Mapeo de controles
    loadControlMappings,
    linkControlToAsset,
    updateControlMapping,
    unlinkControlFromAsset,

    // Ciclo de vida
    loadLifecycleEvents,
    createLifecycleEvent,
    changeAssetStatus,

    // Gestión de cambios
    loadAssetChanges,
    createChange,
    updateChange,
    approveChange,
    completeChange,
    verifyChange,
    rollbackChange
  };

  return <AssetContext.Provider value={value}>{children}</AssetContext.Provider>;
}

// =====================================================
// HOOK
// =====================================================

/**
 * Hook para consumir el contexto de activos en componentes funcionales.
 * Lanza un error descriptivo si se usa fuera de `AssetProvider`.
 *
 * @returns El valor completo del `AssetContextType`
 * @throws Error si el hook se usa fuera del árbol del provider
 *
 * @example
 * const { assets, filteredAssets, metrics, createAsset } = useAssets();
 */
export function useAssets() {
  const context = useContext(AssetContext);
  if (context === undefined) {
    throw new Error('useAssets debe ser usado dentro de un AssetProvider');
  }
  return context;
}
